import React, { useState, useEffect } from 'react';
import { API_URL } from './config';

// Add custom styles
const styles = `
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const VehicleManagement = () => {
  const [vehicles, setVehicles] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null });

  // Form state for adding/editing vehicles
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    image_path: '',
    status: 'available',
    capacity: '',
    base_rate: '',
    service_category: 'Standard',
    features: '',
    display_order: 1
  });

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // Show confirmation dialog
  const showConfirmDialog = (message, onConfirm) => {
    setConfirmDialog({ show: true, message, onConfirm });
  };

  // Handle confirmation
  const handleConfirm = () => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    setConfirmDialog({ show: false, message: '', onConfirm: null });
  };

  // Cancel confirmation
  const handleCancelConfirm = () => {
    setConfirmDialog({ show: false, message: '', onConfirm: null });
  };

  // Fetch vehicle types from API
  const fetchVehicles = async () => {
    console.log('🚗 Fetching vehicles from:', `${API_URL}/api/vehicle-types`);
    try {
      const response = await fetch(`${API_URL}/api/vehicle-types`);
      console.log('📡 Response status:', response.status);
      const data = await response.json();
      console.log('📊 Received data:', data);
      
      if (response.ok) {
        // Handle both array and object responses
        let vehiclesArray = [];
        if (Array.isArray(data)) {
          vehiclesArray = data;
        } else if (data && typeof data === 'object') {
          // If it's an object, try to find the array in common keys
          vehiclesArray = data.data || data.vehicles || data.vehicleTypes || Object.values(data).find(Array.isArray) || [];
        }
        
        setVehicles(vehiclesArray.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)));
        console.log('✅ Vehicles loaded successfully:', vehiclesArray.length);
      } else {
        console.error('❌ API Error:', response.status, data);
        showNotification('Failed to fetch vehicles', 'error');
      }
    } catch (error) {
      console.error('❌ Network Error:', error);
      showNotification('Error fetching vehicles', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch uploaded vehicle images
  const fetchUploadedImages = async () => {
    console.log('🖼️ Fetching uploaded images from:', `${API_URL}/api/vehicle-images`);
    try {
      const response = await fetch(`${API_URL}/api/vehicle-images`);
      console.log('📡 Images response status:', response.status);
      const data = await response.json();
      console.log('🖼️ Received images data:', data);
      
      if (response.ok) {
        setUploadedImages(data.data);
        console.log('✅ Images loaded successfully:', data.data.length);
      } else {
        console.error('❌ Images API Error:', response.status, data);
      }
    } catch (error) {
      console.error('❌ Images Network Error:', error);
    }
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification('Please select an image file', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showNotification('File size must be less than 5MB', 'error');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${API_URL}/api/vehicle-image`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      if (result.success) {
        showNotification('Image uploaded successfully', 'success');
        setFormData({ ...formData, image_path: result.data.filename });
        fetchUploadedImages(); // Refresh uploaded images list
      } else {
        showNotification(result.message || 'Upload failed', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showNotification('Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const vehicleData = {
        ...formData,
        capacity: parseInt(formData.capacity),
        base_rate: parseFloat(formData.base_rate),
        display_order: parseInt(formData.display_order),
        features: formData.features.split(',').map(f => f.trim()).filter(f => f)
      };

      const url = editingVehicle 
        ? `${API_URL}/api/vehicle-types/${editingVehicle.id}`
        : `${API_URL}/api/vehicle-types`;
      
      const method = editingVehicle ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vehicleData)
      });

      if (response.ok) {
        showNotification(
          editingVehicle ? 'Vehicle updated successfully' : 'Vehicle added successfully',
          'success'
        );
        fetchVehicles();
        resetForm();
      } else {
        const error = await response.json();
        showNotification(error.message || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('Submit error:', error);
      showNotification('Operation failed', 'error');
    }
  };

  // Handle service availability toggle
  const toggleServiceAvailability = async (vehicleId, service, newValue) => {
    const serviceField = `available_for_${service}`;
    const serviceName = service.charAt(0).toUpperCase() + service.slice(1);
    
    showConfirmDialog(
      `Are you sure you want to ${newValue ? 'enable' : 'disable'} this vehicle for ${serviceName} service?`,
      async () => {
        try {
          const response = await fetch(`${API_URL}/api/vehicle-types/${vehicleId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ [serviceField]: newValue })
          });

          if (response.ok) {
            showNotification(`Vehicle ${newValue ? 'enabled' : 'disabled'} for ${serviceName} service`, 'success');
            fetchVehicles(); // Refresh the list
          } else {
            const error = await response.json();
            showNotification('Failed to update service availability', 'error');
          }
        } catch (error) {
          console.error('Toggle service availability error:', error);
          showNotification('Failed to update service availability', 'error');
        }
      }
    );
  };

  // Handle vehicle deletion
  const handleDelete = async (vehicleId) => {
    showConfirmDialog('Are you sure you want to delete this vehicle type?', async () => {
      try {
        const response = await fetch(`${API_URL}/api/vehicle-types/${vehicleId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          showNotification('Vehicle deleted successfully', 'success');
          fetchVehicles();
        } else {
          showNotification('Failed to delete vehicle', 'error');
        }
      } catch (error) {
        console.error('Delete error:', error);
        showNotification('Delete failed', 'error');
      }
    });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      image_path: '',
      status: 'available',
      capacity: '',
      base_rate: '',
      features: '',
      display_order: 1,
      // Service availability fields
      available_for_private: true,
      available_for_shared: true,
      available_for_delivery: false,
      available_for_store: false,
      market_segment: 'standard'
    });
    setEditingVehicle(null);
    setShowAddForm(false);
  };

  // Edit vehicle
  const editVehicle = (vehicle) => {
    setFormData({
      ...vehicle,
      features: vehicle.features ? vehicle.features.join(', ') : '',
      // Ensure service fields are included
      available_for_private: vehicle.available_for_private || false,
      available_for_shared: vehicle.available_for_shared || false,
      available_for_delivery: vehicle.available_for_delivery || false,
      available_for_store: vehicle.available_for_store || false,
      market_segment: vehicle.market_segment || 'standard'
    });
    setEditingVehicle(vehicle);
    setShowAddForm(true);
  };

  // Delete uploaded image
  const deleteImage = async (filename) => {
    showConfirmDialog(`Delete image "${filename}"?`, async () => {
      try {
        const response = await fetch(`${API_URL}/api/vehicle-image/${filename}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          showNotification('Image deleted successfully', 'success');
          fetchUploadedImages();
        } else {
          showNotification('Failed to delete image', 'error');
        }
      } catch (error) {
        console.error('Delete image error:', error);
        showNotification('Delete failed', 'error');
      }
    });
  };

  useEffect(() => {
    fetchVehicles();
    fetchUploadedImages();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading vehicle management...</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <style>{styles}</style>
      {/* Notification */}
      {notification.show && (
        <div className={`mb-4 p-4 rounded ${
          notification.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {notification.message}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Service Vehicle Type Management</h2>
        <p className="text-gray-600 mb-2">Manage service vehicle types, images, and settings for rideshare and delivery services</p>
        <div className="flex items-center space-x-4 text-sm">
          <span className="text-gray-500">API: {API_URL}</span>
          {loading && (
            <span className="flex items-center text-blue-600">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></div>
              Loading...
            </span>
          )}
          {!loading && (
            <span className="text-green-600">
              ✓ Connected
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Vehicle List */}
        <div className="xl:col-span-2">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Service Vehicle Types</h3>
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm"
              >
                Add Service Vehicle Type
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Private
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shared
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Delivery
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Store
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vehicles.map((vehicle) => (
                    <tr key={vehicle.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 whitespace-nowrap">
                        {vehicle.image_path && (
                          <img
                            src={`${API_URL}/uploads/vehicle_types/${vehicle.image_path}`}
                            alt={vehicle.display_name}
                            className="h-16 w-16 object-cover rounded-lg shadow-sm"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMCAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zMiAzMkMxNy4yMzA4IDMyIDE1IDIzLjc2OTIgMTUgMjFDMTUgMTguMjMwOCAxNy4yMzA4IDE2IDMyIDIwQzIyLjc2OTIgMTYgMjUgMTguMjMwOCAyNSAyMUMyNSAyMy43NjkyIDIyLjc2OTIgMjYgMzIgMjZaIiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPgo=';
                            }}
                          />
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{vehicle.display_name}</div>
                          <div className="text-sm text-gray-500">{vehicle.name}</div>
                          <div className="text-xs text-gray-400">Capacity: {vehicle.capacity}</div>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-sm text-gray-900 max-w-xs" title={vehicle.description}>
                          <div className="line-clamp-2">
                            {vehicle.description || 'No description available'}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={vehicle.available_for_private || false}
                            onChange={() => toggleServiceAvailability(vehicle.id, 'private', !vehicle.available_for_private)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 peer-checked:hover:bg-green-700"></div>
                        </label>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={vehicle.available_for_shared || false}
                            onChange={() => toggleServiceAvailability(vehicle.id, 'shared', !vehicle.available_for_shared)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 peer-checked:hover:bg-green-700"></div>
                        </label>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={vehicle.available_for_delivery || false}
                            onChange={() => toggleServiceAvailability(vehicle.id, 'delivery', !vehicle.available_for_delivery)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 peer-checked:hover:bg-green-700"></div>
                        </label>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={vehicle.available_for_store || false}
                            onChange={() => toggleServiceAvailability(vehicle.id, 'store', !vehicle.available_for_store)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 peer-checked:hover:bg-green-700"></div>
                        </label>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        ${vehicle.base_rate}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => editVehicle(vehicle)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(vehicle.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Image Upload & Management */}
        <div className="space-y-4">
          {/* Upload Form */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-3">Upload Service Vehicle Image</h3>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Image File
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF up to 5MB
                </p>
              </div>

              {uploading && (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  <span className="text-sm text-gray-600">Uploading...</span>
                </div>
              )}
            </div>
          </div>

          {/* Uploaded Images */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-3">Uploaded Images</h3>
            
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {uploadedImages.length === 0 ? (
                <p className="text-gray-500 text-sm">No images uploaded yet</p>
              ) : (
                uploadedImages.map((image) => (
                  <div key={image.filename} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center flex-1 min-w-0">
                      <img
                        src={image.url}
                        alt={image.filename}
                        className="h-12 w-12 object-cover rounded mr-3 flex-shrink-0"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{image.filename}</div>
                        <div className="text-xs text-gray-500">{(image.size / 1024).toFixed(1)} KB</div>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteImage(image.filename)}
                      className="text-red-600 hover:text-red-800 text-sm flex-shrink-0"
                    >
                      Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Vehicle Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingVehicle ? 'Edit Service Vehicle Type' : 'Add New Service Vehicle Type'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Name</label>
                  <input
                    type="text"
                    required
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Image Path</label>
                  <select
                    value={formData.image_path}
                    onChange={(e) => setFormData({ ...formData, image_path: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">Select an image</option>
                    {uploadedImages.map((image) => (
                      <option key={image.filename} value={image.filename}>
                        {image.filename}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Capacity</label>
                    <input
                      type="number"
                      required
                      value={formData.capacity}
                      onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Base Rate ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.base_rate}
                      onChange={(e) => setFormData({ ...formData, base_rate: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Service Availability</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.available_for_private || false}
                        onChange={(e) => setFormData({ ...formData, available_for_private: e.target.checked })}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Private Rides</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.available_for_shared || false}
                        onChange={(e) => setFormData({ ...formData, available_for_shared: e.target.checked })}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Shared Rides</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.available_for_delivery || false}
                        onChange={(e) => setFormData({ ...formData, available_for_delivery: e.target.checked })}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Regular Delivery</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.available_for_store || false}
                        onChange={(e) => setFormData({ ...formData, available_for_store: e.target.checked })}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Store Delivery</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Market Segment</label>
                  <select
                    value={formData.market_segment || 'standard'}
                    onChange={(e) => setFormData({ ...formData, market_segment: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="standard">Standard</option>
                    <option value="swift">Swift (Premium)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="available">Available</option>
                    <option value="coming_soon">Coming Soon</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Features (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.features}
                    onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                    placeholder="Air Conditioning, 4 Seats, Compact Size"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Order</label>
                  <input
                    type="number"
                    required
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    {editingVehicle ? 'Update' : 'Add'} Service Vehicle Type
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">Confirm Action</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">{confirmDialog.message}</p>
              </div>
            </div>
            <div className="mt-4 flex justify-center space-x-3">
              <button
                onClick={handleCancelConfirm}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleManagement;
